/** @format */

import { create } from "zustand";
import { safeStorage } from "@/utils/storage";
import { getPWAInstallState, getPWAPlatform, type PWAPlatform } from "@/utils/utility";
import { getVisitCount } from "@/utils/visitCounter";

const DISMISS_KEY = "pwa-install-dismissed";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MIN_VISITS = 3;

function wasDismissedRecently(): boolean {
  const dismissed = safeStorage.getItem(DISMISS_KEY);
  if (!dismissed) return false;
  return Date.now() - Number(dismissed) < DISMISS_DURATION_MS;
}

interface PWAInstallStoreState {
  deferredPrompt: BeforeInstallPromptEvent | null;
  showBanner: boolean;
  platform: PWAPlatform;

  setDeferredPrompt: (event: BeforeInstallPromptEvent) => void;
  dismiss: () => void;
  triggerInstall: () => Promise<"accepted" | "dismissed" | null>;
  checkShouldShow: () => void;
  clearBanner: () => void;
}

export const usePWAInstallStore = create<PWAInstallStoreState>((set, get) => ({
  deferredPrompt: null,
  showBanner: false,
  platform: getPWAPlatform(),

  setDeferredPrompt: (event: BeforeInstallPromptEvent) => {
    set({ deferredPrompt: event, platform: { type: "chromium", canPrompt: true } });
    get().checkShouldShow();
  },

  dismiss: () => {
    safeStorage.setItem(DISMISS_KEY, String(Date.now()));
    set({ showBanner: false });
  },

  triggerInstall: async () => {
    const { deferredPrompt } = get();
    if (!deferredPrompt) return null;

    const result = await deferredPrompt.prompt();
    set({ deferredPrompt: null, showBanner: false });
    return result.outcome;
  },

  checkShouldShow: () => {
    const { platform } = get();

    if (platform.type === "unsupported") return;
    if (getPWAInstallState().installed) return;
    if (wasDismissedRecently()) return;
    if (getVisitCount() < MIN_VISITS) return;

    set({ showBanner: true });
  },

  clearBanner: () => {
    set({ showBanner: false, deferredPrompt: null });
  },
}));
